/**
 * HHGlobal Design System Typography
 * Typography styles following the HHGlobal design system specifications.
 */

/**
 * Typefaces
 * Primary: Arial (fallback to system fonts)
 * Fallback: Roboto
 */
export const typefaces = {
  primary: 'Arial',
  fallback: 'Roboto',
} as const;

/**
 * Font weights
 */
export const fontWeights = {
  regular: 400,
  bold: 700,
} as const;

/**
 * Heading Styles
 * Applied to HTML H tags as needed, ensuring HTML semantic structure and visual balance are achieved.
 */
export const headings = {
  h1: {
    fontSize: '20px',
    fontSizeRem: '1.25rem',
    lineHeight: '28px',
    lineHeightRem: '1.75rem',
    fontWeight: fontWeights.bold,
    description: 'Page heading',
  },
  h2: {
    fontSize: '16px',
    fontSizeRem: '1rem',
    lineHeight: '22px',
    lineHeightRem: '1.375rem',
    fontWeight: fontWeights.bold,
    description: 'Modal and section heading',
  },
  h3: {
    fontSize: '16px',
    fontSizeRem: '1rem',
    lineHeight: '22px',
    lineHeightRem: '1.375rem',
    fontWeight: fontWeights.bold,
    description: 'Section heading',
  },
  h4: {
    fontSize: '14px',
    fontSizeRem: '0.875rem',
    lineHeight: '20px',
    lineHeightRem: '1.25rem',
    fontWeight: fontWeights.bold,
    description: 'Container heading',
  },
} as const;

/**
 * Body Styles
 * Applied to HTML paragraph tags, ensuring HTML semantic structure and visual balance are achieved.
 */
export const body = {
  large: {
    fontSize: '14px',
    fontSizeRem: '0.875rem',
    lineHeight: '20px',
    lineHeightRem: '1.25rem',
    fontWeight: fontWeights.regular,
    description: 'Large paragraphs and descriptions',
  },
  base: {
    fontSize: '12px',
    fontSizeRem: '0.75rem',
    lineHeight: '16px',
    lineHeightRem: '1rem',
    fontWeight: fontWeights.regular,
    description: 'Base paragraphs and descriptions',
  },
  small: {
    fontSize: '10px',
    fontSizeRem: '0.625rem',
    lineHeight: '12px',
    lineHeightRem: '0.75rem',
    fontWeight: fontWeights.regular,
    description: 'Small paragraphs and descriptions',
  },
} as const;

/**
 * Text Link Styles
 * Applied to HTML a tags, always bold and underline.
 */
export const links = {
  large: {
    fontSize: '14px',
    fontSizeRem: '0.875rem',
    lineHeight: '20px',
    lineHeightRem: '1.25rem',
    fontWeight: fontWeights.bold,
    textDecoration: 'underline',
    description: 'Large text and labels',
  },
  base: {
    fontSize: '12px',
    fontSizeRem: '0.75rem',
    lineHeight: '16px',
    lineHeightRem: '1rem',
    fontWeight: fontWeights.bold,
    textDecoration: 'underline',
    description: 'Base text and labels',
  },
  small: {
    fontSize: '10px',
    fontSizeRem: '0.625rem',
    lineHeight: '12px',
    lineHeightRem: '0.75rem',
    fontWeight: fontWeights.bold,
    textDecoration: 'underline',
    description: 'Small text and labels',
  },
} as const;

/**
 * Label Styles
 * Applied to HTML inputs, ensuring HTML semantic structure and visual balance are achieved.
 */
export const labels = {
  large: {
    fontSize: '14px',
    fontSizeRem: '0.875rem',
    lineHeight: '16px',
    lineHeightRem: '1rem',
    fontWeight: fontWeights.regular,
    description: 'Large text and labels',
  },
  base: {
    fontSize: '12px',
    fontSizeRem: '0.75rem',
    lineHeight: '14px',
    lineHeightRem: '0.875rem',
    fontWeight: fontWeights.regular,
    description: 'Base text and labels',
  },
  small: {
    fontSize: '10px',
    fontSizeRem: '0.625rem',
    lineHeight: '12px',
    lineHeightRem: '0.75rem',
    fontWeight: fontWeights.regular,
    description: 'Small text and labels',
  },
} as const;

/**
 * Typography configuration object
 */
export const typography = {
  typefaces,
  fontWeights,
  headings,
  body,
  links,
  labels,
} as const;
